import {
  faArrowTrendDown,
  faArrowTrendUp,
  faMinus,
  IconDefinition,
} from '@fortawesome/pro-light-svg-icons';
import { SurveyType } from '../../../../../shared/constants';
import {
  SURVEY_SCALE_BY_TYPE,
  surveyScoreToLabel,
} from '../../../../../shared/surveys';
import { parseDateToLocal } from '../../../../../shared/utils';
import { customError, customSuccess, dimGrey } from '../../../../../mainTheme';
import type {
  SurveyInsightsDistributionEntry,
  SurveyInsightsGranularity,
} from '../../../../../store/hubOwner/types';
import {
  bucketUnitLabel,
  MIN_BUCKETS_FOR_TREND,
  SurveyStats,
  SurveyTrendDirection,
  surveyRowLabel,
} from './surveyInsights';

// Builds the tile contents for SurveyStatsCards. Kept out of the component
// file so the component module only exports components (Vite Fast Refresh).

export type StatCard = {
  key: string;
  label: string;
  value: string;
  // Second line under the value: response label and/or date.
  sub?: string | null;
  description: string;
  icon?: { icon: IconDefinition; color: string; testId?: string };
  // The value is not known yet (insights fetch in flight): draw a placeholder.
  pending?: boolean;
};

// State of the window fetch the tiles are built from.
export type StatCardsStatus = 'ready' | 'loading' | 'unavailable';

export const TREND_LABELS: Record<SurveyTrendDirection, string> = {
  improving: 'Improving',
  declining: 'Declining',
  steady: 'Steady',
  insufficient: 'Not enough data',
};

const TREND_ICONS: Record<SurveyTrendDirection, StatCard['icon']> = {
  improving: { icon: faArrowTrendUp, color: customSuccess },
  declining: { icon: faArrowTrendDown, color: customError },
  steady: { icon: faMinus, color: dimGrey },
  insufficient: undefined,
};

const DATE_FORMAT = 'MMM d, yyyy';

const describeEntry = (
  entry: SurveyInsightsDistributionEntry | null,
  surveyType: SurveyType,
): string | null =>
  entry
    ? `${entry.label ?? surveyScoreToLabel(surveyType, entry.score) ?? 'N/A'} · ${
        entry.count
      }×`
    : null;

const formatDelta = (delta: number | null): string => {
  if (delta === null) return '';
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta} vs earlier in window`;
};

export const UNAVAILABLE_NOTE = 'Could not load';

export const buildStatCards = (
  stats: SurveyStats,
  surveyType: SurveyType,
  windowLabel: string,
  granularity: SurveyInsightsGranularity | null | undefined,
  // 'loading' while the insights fetch is in flight: window tiles show a
  // placeholder instead of zeros. 'unavailable' when it failed: they read
  // N/A instead of asserting zero responses.
  status: StatCardsStatus = 'ready',
  // Same for the latest tile, which comes from the response rows: those load
  // separately, so they have their own state.
  rowsStatus: StatCardsStatus = 'ready',
): StatCard[] => {
  const { max } = SURVEY_SCALE_BY_TYPE[surveyType];
  const outOf = (score: number | null | undefined) =>
    score == null ? 'N/A' : `${score} / ${max}`;
  const windowNote = windowLabel.toLowerCase();
  const cards: StatCard[] = [
    {
      key: 'count',
      label: 'Responses',
      value: String(stats.count),
      sub: windowNote,
      description: `Days in the ${windowNote} on which this patient answered this survey. Several answers on one day are counted as per survey cadence.`,
    },
    {
      key: 'average',
      label: 'Average score',
      value: outOf(stats.average),
      sub: windowNote,
      description: `Mean of the daily scores on the 1-${max} scale over the ${windowNote} (latest answer per day).`,
    },
    {
      key: 'lowest',
      label: 'Lowest score',
      value: outOf(stats.lowest?.score),
      sub: describeEntry(stats.lowest, surveyType),
      description: `Lowest score given in the ${windowNote}, with the number of days it was given.`,
    },
    {
      key: 'highest',
      label: 'Highest score',
      value: outOf(stats.highest?.score),
      sub: describeEntry(stats.highest, surveyType),
      description: `Highest score given in the ${windowNote}, with the number of days it was given.`,
    },
    {
      key: 'latest',
      label: 'Latest score',
      value: outOf(stats.latest?.score),
      sub: stats.latest
        ? `${surveyRowLabel(stats.latest, surveyType)} · ${
            stats.latest.received_at
              ? parseDateToLocal(stats.latest.received_at, DATE_FORMAT)
              : 'N/A'
          }`
        : null,
      description: `The most recent response to this survey in the ${windowNote}.`,
    },
    {
      key: 'trend',
      label: 'Trend',
      value: TREND_LABELS[stats.trend],
      sub:
        stats.trend === 'insufficient'
          ? `Needs ${MIN_BUCKETS_FOR_TREND}+ answered ${bucketUnitLabel(granularity)}`
          : formatDelta(stats.trendDelta),
      description:
        'Compares the average of the most recent half of the answered periods in the window with the average of the earlier half.',
      icon: TREND_ICONS[stats.trend] && {
        ...TREND_ICONS[stats.trend]!,
        testId: `trend-icon-${stats.trend}`,
      },
    },
  ];
  const applyStatus = (card: StatCard, s: StatCardsStatus): StatCard =>
    s === 'ready'
      ? card
      : s === 'loading'
        ? { ...card, value: '', sub: null, icon: undefined, pending: true }
        : { ...card, value: 'N/A', sub: UNAVAILABLE_NOTE, icon: undefined };
  return cards.map((card) =>
    applyStatus(card, card.key === 'latest' ? rowsStatus : status),
  );
};
